<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-4">
        <h2 class="font-weight-bolder">{{ pageTitle }}</h2>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined class="mr-2" @click="goBack">
          {{ $t('back') }}
        </v-btn>
      </div>

      <v-card v-if="detail" elevation="0" class="px-5">
        <v-card-text class="text-body-1 black--text pa-0">
          <div class="px-4 py-4">
            <!-- Progress summary -->
            <div class="bulk-result-progress-section">
              <span class="bulk-result-progress-title text--secondary">
                {{ $t('memberDataSection.greenCoffee.bulkImport.importCompleted') }}
              </span>
              <div class="bulk-result-progress-row mt-2">
                <v-progress-linear
                  rounded
                  height="12"
                  :value="importProgressPercent"
                  color="kenyaGreen"
                  background-color="green2"
                  class="bulk-result-progress-bar"
                ></v-progress-linear>
                <span
                  class="bulk-result-progress-percent kenyaGreen--text"
                >{{ importProgressPercent }}%</span>
              </div>
              <p class="bulk-result-progress-status text--secondary mt-2 mb-0">
                <span class="font-weight-bold">{{ importedRecordsCount }}</span>
                {{ $t('of') }}
                <span class="font-weight-bold">{{ detail.totalRecordsCount }}</span>
                {{ $t('recordsImported') }}
              </p>
            </div>

            <!-- Summary cards -->
            <v-row class="bulk-result-summary-row mt-4" dense>
              <v-col v-for="card in summaryCards" :key="card.key" cols="12" sm="4">
                <div
                  class="bulk-result-summary-card"
                  :style="{ borderLeftColor: themeColor(card.color) }"
                >
                  <div
                    class="bulk-result-summary-card__value"
                    :style="{ color: themeColor(card.color) }"
                  >
                    {{ card.count }}
                  </div>
                  <div class="bulk-result-summary-card__label text--secondary">{{ card.label }}</div>
                </div>
              </v-col>
            </v-row>

            <!-- Filter chips (one per issue category) -->
            <div class="bulk-result-filter-chips mt-5 d-flex flex-wrap">
              <button
                v-for="chip in filterChips"
                :key="chip.value"
                type="button"
                class="bulk-result-filter-chip"
                :style="chipStyle(chip)"
                @click="resultFilter = chip.value"
              >
                <v-icon
                  v-if="chip.icon"
                  small
                  :color="chipIconColor(chip)"
                  class="mr-1"
                >
                  {{ chip.icon }}
                </v-icon>
                {{ chip.label }} ({{ chip.count }})
              </button>
            </div>

            <!-- Bulk resolution alert -->
            <div
              v-if="issuesRecordsCount > 0"
              class="bulk-result-issues-alert mt-4"
              :style="{
                backgroundColor: themeColor('secondary2light'),
                borderColor: themeColor('third'),
              }"
            >
              <p class="bulk-result-issues-alert__heading black--text mb-2">
                {{ $t('memberDataSection.greenCoffee.bulkImport.issuesDetectedHeading') }}
              </p>
              <p class="bulk-result-issues-alert__body mb-3">
                {{ $t('memberDataSection.greenCoffee.bulkImport.issuesDetectedDescription') }}
              </p>
              <p class="bulk-result-issues-alert__prompt black--text mb-3">
                {{ $t('memberDataSection.greenCoffee.bulkImport.bulkResolutionPrompt') }}
              </p>

              <div class="bulk-result-bulk-resolution-options">
                <label class="bulk-result-bulk-resolution-option">
                  <input
                    v-model="bulkResolutionAction"
                    type="radio"
                    name="green-coffee-bulk-resolution-action"
                    value="delete"
                    :disabled="isLocked || actionLoading"
                  />
                  <div>
                    <p class="bulk-result-bulk-resolution-option__title black--text mb-1">
                      {{ $t('memberDataSection.greenCoffee.bulkImport.bulkDeleteRecordsTitle') }}
                    </p>
                    <p class="bulk-result-bulk-resolution-option__body mb-0">
                      {{ $t('memberDataSection.greenCoffee.bulkImport.bulkDeleteRecordsDescription') }}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <!-- Records table (rows clickable → detail dialog) -->
            <v-card class="py-0 mt-4 bulk-result-table-card" elevation="1">
              <v-data-table
                item-key="id"
                disable-pagination
                :headers="tableHeaders"
                :items="filteredRecords"
                hide-default-footer
                class="bulk-result-table"
                @click:row="openDetail"
              >
                <template v-slot:item.lotId="{ item }">
                  {{ getPayloadField(item, "id") }}
                </template>
                <template v-slot:item.outturnNumber="{ item }">
                  <div class="d-flex align-center">
                    <span
                      :class="{ 'red--text': shouldHighlightOutturn(item) }"
                      class="text-body-2"
                    >
                      {{ getOutturnNumber(item) }}
                    </span>
                    <v-tooltip v-if="shouldHighlightOutturn(item)" bottom max-width="360">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" small color="third" class="ml-1">
                          mdi-alert
                        </v-icon>
                      </template>
                      <div class="bulk-result-issue-tooltip">
                        <div class="font-weight-bold">{{ getRecordIssueTitle(item) }}</div>
                        <div class="mt-1">{{ getRecordIssueDescription(item) }}</div>
                      </div>
                    </v-tooltip>
                  </div>
                </template>
                <template v-slot:item.grower="{ item }">
                  <div class="d-flex align-center">
                    <div
                      class="bulk-result-grower-cell"
                      :class="{ 'red--text': shouldHighlightGrower(item) }"
                    >
                      <div v-if="getGrowerCode(item)" class="text-body-2">{{ getGrowerCode(item) }}</div>
                      <div v-if="getGrowerName(item)" class="text-body-2">{{ getGrowerName(item) }}</div>
                      <span v-if="!getGrowerCode(item) && !getGrowerName(item)">—</span>
                    </div>
                    <v-tooltip v-if="shouldHighlightGrower(item)" bottom max-width="360">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" small color="third" class="ml-1 flex-shrink-0">
                          mdi-alert
                        </v-icon>
                      </template>
                      <div class="bulk-result-issue-tooltip">
                        <div class="font-weight-bold">{{ getRecordIssueTitle(item) }}</div>
                        <div class="mt-1">{{ getRecordIssueDescription(item) }}</div>
                      </div>
                    </v-tooltip>
                  </div>
                </template>
                <template v-slot:item.coffeeType="{ item }">
                  {{ getPayloadField(item, "coffeeType") }}
                </template>
                <template v-slot:item.status="{ item }">
                  <v-btn
                    small
                    rounded
                    depressed
                    min-width="140"
                    height="34"
                    class="bulk-result-status-btn non-clickable"
                    :style="statusPillStyle(item)"
                  >
                    {{ statusLabel(item) }}
                  </v-btn>
                </template>
                <template v-slot:item.action="{ item }">
                  <div class="d-flex align-center justify-center flex-nowrap bulk-result-actions">
                    <v-btn
                      icon
                      small
                      elevation="2"
                      class="mr-1"
                      :disabled="isLocked || actionLoading"
                      @click.stop="editRecord(item)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      elevation="2"
                      :disabled="isLocked || actionLoading"
                      @click.stop="deleteRecord(item)"
                    >
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card>

            <v-divider class="my-5 px-0"></v-divider>
            <v-card-actions class="px-0">
              <v-spacer></v-spacer>
              <v-btn
                outlined
                color="primary"
                class="mr-2"
                width="112"
                :disabled="isLocked || actionLoading || importLoading"
                @click="cancelImportAction"
              >
                {{ $t('cancel') }}
              </v-btn>
              <v-btn
                color="primary"
                class="btn-11 btn-22 white--text"
                :disabled="!canContinue"
                :loading="importLoading || actionLoading"
                @click="onBottomContinue"
              >
                {{ $t('continue') }}
              </v-btn>
            </v-card-actions>
          </div>
        </v-card-text>
      </v-card>

      <v-card v-else-if="!loading" elevation="0" class="px-5 pa-8 text-center text--secondary">
        {{ $t('memberDataSection.greenCoffee.noDataFound') }}
        <div class="mt-4">
          <v-btn color="primary" outlined @click="retryImport">
            {{ $t('memberDataSection.greenCoffee.bulkImport.tryAgain') }}
          </v-btn>
        </div>
      </v-card>

      <GreenCoffeeBulkImportDetailDialog
        v-model="detailDialog"
        :record="selectedRecord"
        :issue-title="selectedRecord ? getRecordIssueTitle(selectedRecord) : ''"
        :issue-description="selectedRecord ? getRecordIssueDescription(selectedRecord) : ''"
        @edit="onDetailEdit"
      />

      <GreenCoffeeBulkImportEditDialog
        v-model="editDialog"
        :record="editingRecord"
        :loading="actionLoading"
        @save="onEditSave"
      />

      <ConfirmBox
        :dialog="confirm.dialog"
        :title="confirm.title"
        :message="confirm.message"
        :confirmBtnText="confirm.confirmBtnText"
        :cancelBtnText="$t('cancel')"
        @cancel:action="onConfirmCancel"
        @continue:action="onConfirmContinue"
      />

      <GreenCoffeeBulkImportSuccessDialog
        v-model="successDialog"
        :count="importedCount"
        @close="onSuccessClose"
      />

      <v-overlay :value="actionLoading || importLoading || loading" z-index="300">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-container>
  </div>
</template>

<script>
import {
  BULK_ISSUE_I18N_KEYS,
  isDataDiscrepancyRecord,
  isSuccessRecord,
  isFailedRecord,
  isOutturnRelatedIssue,
  isGrowerRelatedIssue,
  resolveBulkIssueType,
  mapServerRecord,
  mapServerImport,
  SERVER_ERROR_CODE_I18N,
  SERVER_ERROR_CODE_LABEL_I18N,
  SERVER_BULK_IMPORT_STATUS,
} from "@/utils/greenCoffeeBulkImport";
import GreenCoffeeBulkImportService from "@/_services/GreenCoffeeBulkImportService";
import GreenCoffeeBulkImportEditDialog from "./GreenCoffeeBulkImportEditDialog.vue";
import GreenCoffeeBulkImportDetailDialog from "./GreenCoffeeBulkImportDetailDialog.vue";
import GreenCoffeeBulkImportSuccessDialog from "./GreenCoffeeBulkImportSuccessDialog.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";

const RESULT_FILTER = { ALL: "all", SUCCESS: "success" };

export default {
  name: "GreenCoffeeBulkImportResult",
  components: {
    GreenCoffeeBulkImportEditDialog,
    GreenCoffeeBulkImportDetailDialog,
    GreenCoffeeBulkImportSuccessDialog,
    ConfirmBox,
  },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      detail: null,
      bulkImportId: null,
      resultFilter: RESULT_FILTER.ALL,
      bulkResolutionAction: "",
      loading: false,
      actionLoading: false,
      importLoading: false,
      detailDialog: false,
      selectedRecord: null,
      editDialog: false,
      editingRecord: null,
      successDialog: false,
      importedCount: 0,
      confirm: { dialog: false, title: "", message: "", confirmBtnText: "", handler: null },
      tableHeaders: [
        { text: this.$t("memberDataSection.greenCoffee.bulkImport.lotId"), value: "lotId", class: "bulk-result-table-header" },
        { text: this.$t("memberDataSection.greenCoffee.outturnNumber"), value: "outturnNumber", class: "bulk-result-table-header" },
        { text: this.$t("memberDataSection.greenCoffee.bulkImport.grower"), value: "grower", class: "bulk-result-table-header" },
        { text: this.$t("memberDataSection.greenCoffee.bulkImport.coffeeType"), value: "coffeeType", class: "bulk-result-table-header" },
        { text: this.$t("oma.status"), value: "status", align: "center", class: "bulk-result-table-header" },
        { text: this.$t("oma.action"), value: "action", align: "center", sortable: false, class: "bulk-result-table-header bulk-result-action-col", width: "120px" },
      ],
    };
  },
  computed: {
    isLocked() {
      return (
        this.detail?.status === SERVER_BULK_IMPORT_STATUS.CANCELLED ||
        this.detail?.status === SERVER_BULK_IMPORT_STATUS.SUCCESS
      );
    },
    canImport() {
      return (
        !this.isLocked &&
        !this.actionLoading &&
        !this.importLoading &&
        Boolean(this.bulkImportId) &&
        this.successRecordsCount > 0 &&
        // Every record must be resolved to SUCCESS — no outstanding issues.
        this.issuesRecordsCount === 0
      );
    },
    // Drives the single bottom "Continue" button (parchment-delivery-style):
    // with issues → a bulk resolution must be selected; without → ready to import.
    canContinue() {
      if (this.isLocked || this.actionLoading || this.importLoading || !this.bulkImportId) {
        return false;
      }
      if (this.issuesRecordsCount > 0) {
        return this.bulkResolutionAction === "delete" && this.discrepancyRecords.length > 0;
      }
      return this.successRecordsCount > 0;
    },
    pageTitle() {
      return this.$t("memberDataSection.greenCoffee.bulkImport.title");
    },
    breadcrumbs() {
      return [
        { text: this.$t("dashboard"), disabled: false, to: { name: "AdminDashboard" }, exact: true },
        { text: this.$t("dashboardSection.cropOverview"), disabled: true, href: "#" },
        { text: this.$t("dashboardSection.greenCoffee"), disabled: false, to: { name: "GreenCoffee" }, exact: true },
        { text: this.pageTitle, disabled: true, href: "#" },
      ];
    },
    allRecords() {
      return this.detail?.records || [];
    },
    successRecords() {
      return this.allRecords.filter((r) => isSuccessRecord(r));
    },
    failedRecords() {
      return this.allRecords.filter((r) => isFailedRecord(r));
    },
    discrepancyRecords() {
      return this.allRecords.filter((r) => isDataDiscrepancyRecord(r));
    },
    importedRecordsCount() {
      if (this.detail?.importedRecordsCount != null) return this.detail.importedRecordsCount;
      return this.successRecords.length;
    },
    importProgressPercent() {
      const total = this.detail?.totalRecordsCount || 0;
      if (!total) return 0;
      return Math.min(100, Math.round((this.importedRecordsCount / total) * 100));
    },
    totalRecordsCount() {
      return this.detail?.totalRecordsCount || 0;
    },
    successRecordsCount() {
      return this.detail?.successRecordsCount ?? this.successRecords.length;
    },
    issuesRecordsCount() {
      return this.detail?.discrepancyRecordsCount ?? this.discrepancyRecords.length;
    },
    /** Count of records that carry each distinct error code. */
    errorCodeCounts() {
      const counts = {};
      this.discrepancyRecords.forEach((r) => {
        (r.errorCodes || []).forEach((code) => {
          counts[code] = (counts[code] || 0) + 1;
        });
      });
      return counts;
    },
    summaryCards() {
      const t = (key) => this.$t(`memberDataSection.greenCoffee.bulkImport.${key}`);
      return [
        { key: "total", color: "gray", label: t("tabTotalRecords"), count: this.totalRecordsCount },
        { key: "success", color: "kenyaGreen", label: t("tabSuccess"), count: this.successRecordsCount },
        { key: "issues", color: "third", label: t("tabIssues"), count: this.issuesRecordsCount },
      ];
    },
    filterChips() {
      const t = (key) => this.$t(`memberDataSection.greenCoffee.bulkImport.${key}`);
      const chips = [
        { value: RESULT_FILTER.ALL, label: t("filterAll"), count: this.totalRecordsCount, icon: "mdi-check", color: "kenyaGreen" },
        { value: RESULT_FILTER.SUCCESS, label: t("filterSuccess"), count: this.successRecordsCount, icon: "mdi-check", color: "kenyaGreen" },
      ];
      Object.keys(this.errorCodeCounts).forEach((code) => {
        const labelKey = SERVER_ERROR_CODE_LABEL_I18N[code];
        chips.push({
          value: code,
          label: labelKey ? t(labelKey) : code,
          count: this.errorCodeCounts[code],
          icon: "mdi-alert",
          color: "third",
        });
      });
      return chips;
    },
    filteredRecords() {
      if (this.resultFilter === RESULT_FILTER.SUCCESS) return this.successRecords;
      if (this.resultFilter === RESULT_FILTER.ALL) return this.allRecords;
      // A specific error-code filter.
      return this.allRecords.filter((r) => (r.errorCodes || []).includes(this.resultFilter));
    },
  },
  watch: {
    id() {
      this.fetchDetail();
    },
  },
  mounted() {
    this.fetchDetail();
  },
  methods: {
    async fetchDetail() {
      this.bulkImportId = this.id != null ? this.id : null;
      if (!this.bulkImportId) {
        this.detail = null;
        return;
      }
      this.loading = true;
      try {
        const res = await GreenCoffeeBulkImportService.getImportById(this.bulkImportId);
        if (res && res.success !== false && res.data) {
          this.detail = mapServerImport(res.data);
          this.bulkImportId = this.detail.bulkImportId || this.bulkImportId;
        } else {
          this.detail = null;
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.detail = null;
        this.notifyError(err && err.message);
      } finally {
        this.loading = false;
      }
    },
    refreshDetailCounts() {
      if (!this.detail) return;
      this.detail.totalRecordsCount = this.detail.records.length;
      this.detail.successRecordsCount = this.successRecords.length;
      this.detail.failedRecordsCount = this.failedRecords.length;
      this.detail.discrepancyRecordsCount = this.discrepancyRecords.length;
      this.detail.errorRecordsCount = this.discrepancyRecords.length;
      this.detail.importedRecordsCount = this.successRecords.length;
    },
    themeColor(name) {
      const theme = this.$vuetify.theme;
      const variant = theme.dark ? "dark" : "light";
      return (theme.themes[variant] && theme.themes[variant][name]) || "";
    },
    getPayloadField(item, field) {
      const p = item?.payloadJsonData || item?.payload || {};
      const val = p[field];
      return val != null && val !== "" ? val : "—";
    },
    getOutturnNumber(item) {
      return this.getPayloadField(item, "outturnNumber");
    },
    shouldHighlightOutturn(item) {
      if (!isDataDiscrepancyRecord(item)) return false;
      const md = item.errorMetadata || {};
      if (md.outturnNumber && md.outturnNumber.length) return true;
      return isOutturnRelatedIssue(resolveBulkIssueType(item));
    },
    shouldHighlightGrower(item) {
      if (!isDataDiscrepancyRecord(item)) return false;
      const md = item.errorMetadata || {};
      if ((md.growerName && md.growerName.length) || (md.growerCode && md.growerCode.length)) return true;
      return isGrowerRelatedIssue(resolveBulkIssueType(item));
    },
    getRecordIssueTitle(item) {
      const issueType = resolveBulkIssueType(item);
      const keys = BULK_ISSUE_I18N_KEYS[issueType];
      if (!keys) return "";
      return this.$t(`memberDataSection.greenCoffee.bulkImport.${keys.title}`);
    },
    getRecordIssueDescription(item) {
      const codes = item.errorCodes || [];
      if (codes.length) {
        const msgs = codes
          .map((code) => {
            const key = SERVER_ERROR_CODE_I18N[code];
            return key ? this.$t(`memberDataSection.greenCoffee.bulkImport.${key}`) : "";
          })
          .filter(Boolean);
        if (msgs.length) return msgs.join(" ");
      }
      const issueType = resolveBulkIssueType(item);
      const keys = BULK_ISSUE_I18N_KEYS[issueType];
      if (!keys) return "";
      return this.$t(`memberDataSection.greenCoffee.bulkImport.${keys.description}`, item.issueContext || {});
    },
    getGrowerCode(item) {
      const p = item?.payloadJsonData || item?.payload || {};
      return p.grower != null ? String(p.grower).trim() : "";
    },
    getGrowerName(item) {
      const p = item?.payloadJsonData || item?.payload || {};
      return p.growerName != null ? String(p.growerName).trim() : "";
    },
    statusLabel(item) {
      if (isDataDiscrepancyRecord(item)) {
        return this.$t("memberDataSection.greenCoffee.bulkImport.issuesDetected");
      }
      if (isSuccessRecord(item)) return this.$t("success");
      return this.$t("failed");
    },
    statusPillStyle(item) {
      let mainName = "red";
      let bgName = "red2";
      if (isDataDiscrepancyRecord(item)) {
        mainName = "third";
        bgName = "secondary2light";
      } else if (isSuccessRecord(item)) {
        mainName = "kenyaGreen";
        bgName = "green2";
      }
      const main = this.themeColor(mainName);
      return { color: main, backgroundColor: this.themeColor(bgName), border: `2px solid ${main}` };
    },
    chipStyle(chip) {
      const active = this.resultFilter === chip.value;
      if (active) {
        return { backgroundColor: this.themeColor(chip.color), color: this.themeColor("white") };
      }
      return { backgroundColor: this.themeColor("themeGrey"), color: this.themeColor("lightBlack") };
    },
    chipIconColor(chip) {
      return this.resultFilter === chip.value ? "white" : chip.color;
    },

    // ── Detail dialog ───────────────────────────────────────────────────
    openDetail(item) {
      this.selectedRecord = item;
      this.detailDialog = true;
    },
    onDetailEdit(record) {
      this.detailDialog = false;
      this.editRecord(record);
    },

    // ── Edit ────────────────────────────────────────────────────────────
    editRecord(item) {
      this.editingRecord = item;
      this.editDialog = true;
    },
    async onEditSave({ recordId, payload }) {
      this.actionLoading = true;
      try {
        const res = await GreenCoffeeBulkImportService.updateRecord(recordId, payload);
        if (res && res.success !== false && res.data) {
          this.replaceRecord(mapServerRecord(res.data));
          this.refreshDetailCounts();
          this.editDialog = false;
          this.editingRecord = null;
          this.$notify({
            type: "success",
            text: res.message || this.$t("memberDataSection.greenCoffee.bulkImport.recordUpdatedSuccess"),
          });
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.actionLoading = false;
      }
    },
    replaceRecord(mapped) {
      if (!this.detail || !this.detail.records) return;
      const idx = this.detail.records.findIndex((r) => r.id === mapped.id);
      if (idx >= 0) this.$set(this.detail.records, idx, mapped);
    },

    // ── Delete ──────────────────────────────────────────────────────────
    deleteRecord(item) {
      this.openConfirm({
        title: this.$t("memberDataSection.greenCoffee.bulkImport.deleteRecord"),
        message: this.$t("memberDataSection.greenCoffee.bulkImport.deleteRecordConfirm"),
        confirmBtnText: this.$t("delete"),
        handler: () => this.performDelete([item.id]),
      });
    },
    onBottomContinue() {
      // No outstanding issues → import the valid records (same confirm as before).
      if (this.issuesRecordsCount === 0) {
        this.runImportAction();
        return;
      }
      // Issues remain → apply the selected bulk resolution. The only option is
      // "delete discrepancies"; the remaining valid rows are then imported in the
      // same step (single Continue, like the parchment-delivery flow).
      if (this.bulkResolutionAction !== "delete") return;
      const ids = this.discrepancyRecords.map((r) => r.id);
      if (!ids.length) return;
      this.openConfirm({
        title: this.$t("memberDataSection.greenCoffee.bulkImport.bulkDeleteRecordsTitle"),
        message: this.$t("memberDataSection.greenCoffee.bulkImport.deleteDiscrepancyConfirm", { count: ids.length }),
        confirmBtnText: this.$t("continue"),
        handler: async () => {
          await this.performDelete(ids);
          if (this.issuesRecordsCount === 0 && this.successRecordsCount > 0) {
            await this.performImport();
          }
        },
      });
    },
    async performDelete(ids) {
      if (!this.bulkImportId || !ids.length) return;
      this.actionLoading = true;
      try {
        const res = await GreenCoffeeBulkImportService.deleteRecords(this.bulkImportId, ids);
        if (res && res.success !== false) {
          const idSet = new Set(ids);
          this.detail.records = this.detail.records.filter((r) => !idSet.has(r.id));
          this.refreshDetailCounts();
          if (this.resultFilter !== RESULT_FILTER.ALL && this.resultFilter !== RESULT_FILTER.SUCCESS) {
            if (!this.errorCodeCounts[this.resultFilter]) this.resultFilter = RESULT_FILTER.ALL;
          }
          this.$notify({
            type: "success",
            text: res.message || this.$t("memberDataSection.greenCoffee.bulkImport.recordsDeletedSuccess"),
          });
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.actionLoading = false;
      }
    },

    // ── Cancel import ───────────────────────────────────────────────────
    cancelImportAction() {
      if (!this.bulkImportId) {
        this.goBack();
        return;
      }
      this.openConfirm({
        title: this.$t("memberDataSection.greenCoffee.bulkImport.cancelImportTitle"),
        message: this.$t("memberDataSection.greenCoffee.bulkImport.cancelImportConfirm"),
        confirmBtnText: this.$t("memberDataSection.greenCoffee.bulkImport.cancelImportConfirmBtn"),
        handler: () => this.performCancel(),
      });
    },
    async performCancel() {
      this.actionLoading = true;
      try {
        const res = await GreenCoffeeBulkImportService.cancelImport(this.bulkImportId);
        if (res && res.success !== false) {
          if (this.detail) this.detail.status = SERVER_BULK_IMPORT_STATUS.CANCELLED;
          this.$notify({
            type: "success",
            text: res.message || this.$t("memberDataSection.greenCoffee.bulkImport.importCancelledSuccess"),
          });
          this.goBack();
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.actionLoading = false;
      }
    },

    // ── Run import ──────────────────────────────────────────────────────
    runImportAction() {
      if (!this.canImport) return;
      this.openConfirm({
        title: this.$t("oma.import"),
        message: this.$t("memberDataSection.greenCoffee.bulkImport.importConfirm", { count: this.successRecordsCount }),
        confirmBtnText: this.$t("oma.import"),
        handler: () => this.performImport(),
      });
    },
    async performImport() {
      this.importLoading = true;
      try {
        // dryMillingUserId is intentionally omitted — the current UI exposes no
        // such selector. Add it to this payload if/when the UI provides one.
        const res = await GreenCoffeeBulkImportService.runImport(this.bulkImportId);
        if (res && res.success !== false) {
          if (this.detail) this.detail.status = SERVER_BULK_IMPORT_STATUS.SUCCESS;
          this.importedCount =
            res.data && res.data.importedCount != null
              ? res.data.importedCount
              : this.successRecordsCount;
          this.successDialog = true;
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.importLoading = false;
      }
    },
    onSuccessClose() {
      this.successDialog = false;
      this.$router.push({ name: "GreenCoffee" });
    },

    // ── Confirm dialog plumbing ─────────────────────────────────────────
    openConfirm({ title, message, confirmBtnText, handler }) {
      this.confirm = {
        dialog: true,
        title: title || "",
        message: message || "",
        confirmBtnText: confirmBtnText || this.$t("confirm"),
        handler: handler || null,
      };
    },
    onConfirmCancel() {
      this.confirm.dialog = false;
      this.confirm.handler = null;
    },
    onConfirmContinue() {
      const handler = this.confirm.handler;
      this.confirm.dialog = false;
      this.confirm.handler = null;
      if (typeof handler === "function") handler();
    },
    notifyError(message) {
      this.$notify({
        type: "error",
        text: message || this.$t("memberDataSection.greenCoffee.bulkImport.genericError"),
      });
    },
    goBack() {
      this.$router.push({ name: "GreenCoffee" });
    },
    retryImport() {
      this.runImportAction();
    },
  },
};
</script>

<style lang="scss" scoped>
.bulk-result-progress-title {
  font-size: 14px;
}

.bulk-result-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bulk-result-progress-bar {
  flex: 1 1 auto;
}

.bulk-result-progress-percent {
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  min-width: 40px;
  text-align: right;
}

.bulk-result-progress-status {
  font-size: 14px;
}

.bulk-result-summary-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 20px 20px 20px 24px;
  border-left: 6px solid transparent;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.bulk-result-summary-card__value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1.1;
}

.bulk-result-summary-card__label {
  font-size: 14px;
  margin-top: 4px;
}

.bulk-result-filter-chips {
  gap: 12px;
}

.bulk-result-filter-chip {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.bulk-result-issues-alert {
  border: 1px solid;
  border-radius: 8px;
  padding: 20px 24px;
}

.bulk-result-issues-alert__heading {
  font-weight: 700;
  font-size: 15px;
}

.bulk-result-issues-alert__body {
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 0;
}

.bulk-result-issues-alert__prompt {
  font-size: 14px;
  font-weight: 600;
}

.bulk-result-bulk-resolution-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bulk-result-bulk-resolution-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  cursor: pointer;
}

.bulk-result-bulk-resolution-option input {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid var(--primary-color, #00bd73);
  border-radius: 50%;
  background: #fff;
}

.bulk-result-bulk-resolution-option input:checked {
  border-color: var(--primary-color, #00bd73);
  background-image: radial-gradient(circle, var(--primary-color, #00bd73) 0 42%, transparent 48%);
}

.bulk-result-bulk-resolution-option__title {
  font-size: 14px;
  font-weight: 600;
}

.bulk-result-bulk-resolution-option__body {
  font-size: 13px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.6);
}

.bulk-result-table-card {
  max-height: 520px;
  overflow: auto;
}

.bulk-result-table ::v-deep tbody tr {
  cursor: pointer;
}

.bulk-result-table ::v-deep thead th span {
  text-transform: uppercase;
  font-weight: 600;
}

.bulk-result-status-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}

.non-clickable {
  pointer-events: none;
  cursor: default;
}

.bulk-result-table ::v-deep .bulk-result-action-col {
  white-space: nowrap;
}

.bulk-result-table ::v-deep td:last-child {
  white-space: nowrap;
}

.bulk-result-actions {
  gap: 4px;
}

.bulk-result-grower-cell {
  line-height: 1.35;
  white-space: normal;
}

.bulk-result-issue-tooltip {
  max-width: 340px;
  line-height: 1.45;
  font-size: 13px;
}
</style>
